import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  useApiClientsOrNull,
  useStudioTemplates,
  lookupChannelItemsBatch,
  STATUS_REPORTING_TEMPLATE_TYPES,
  StudioTemplateGalleryOrganism,
  StudioTemplateDrawerOrganism,
  TagAtom,
  DialogWrapper,
  InputAtom,
  PrimeReactButton as ButtonAtom,
  MessageAtom,
  ChoiceCardGroupAtom,
  ARC_CANVAS_STARTERS,
  applyStarter,
  definitionToModel,
  modelToDefinition,
  type StudioGallerySection,
  type StudioGalleryStatusFilter,
  type ChannelItemLookupEntry,
  slugifyAspectName,
} from 'design-system/my-lib';
import type { StudioTemplateCard } from 'types/studio';
import type { ChannelItemType } from 'types/channel';
import type { ArcTemplateDefinition } from 'types/arc';
import { ARC_ARCHETYPES, archetypeRules, type ArcArchetype } from '@center-platform/arcs/archetype';
import type { AppDispatch } from '@center-pwa/store/store';
import { createArcTemplate as createArcTemplateThunk } from '@center-pwa/reducers/arcTemplatesReducer';
import { StudioBuilder } from './builders';
import { studioRouteForTemplate } from './studioRouteForTemplate';
import './StudioTypeGalleryPage.scss';

export interface StudioTypeGalleryPageProps {
  builder: StudioBuilder;
}

/** Second-section type for the Approach builder (spec SC2 "Sphere Types"). No other builder's
 * `templateTypes` carries more than one entry today, so this is the only section that needs a
 * non-default label; every other type falls back to `studio.gallery.section.<type>`. */
const SECTION_LABEL_KEYS: Record<string, string> = {
  approach: 'studio.gallery.section.approach',
  schema: 'studio.gallery.section.schema',
  arc_template: 'studio.gallery.section.arc_template',
  aspect: 'studio.gallery.section.aspect',
};

/** Mirrors `MAX_ITEMS` in schema-service's `routes/channels/lookupItemsBatch.ts`. */
const BATCH_LOOKUP_CHUNK = 100;

function distributionKey(card: StudioTemplateCard): string {
  return `${card.type}::${card.systemName}`;
}

function matchesSearch(card: StudioTemplateCard, term: string): boolean {
  if (!term) return true;
  const haystack = `${card.displayName} ${card.description ?? ''}`.toLowerCase();
  return haystack.includes(term);
}

/**
 * The `browse`-status builder body (EAINT-873). Owns the gallery's data (`useStudioTemplates`),
 * search + status filter, and the batched distribution lookup — `StudioTemplateGalleryOrganism`
 * is a pure pass-through that renders whatever it's handed, so every permission decision lives
 * server-side in `GET /studio/templates`'s fail-closed bulk `view` filter (this page adds no
 * client-side re-filtering, `galleryDoesNotCallAdminHooks`). Opening a card sets `?template=`;
 * `StudioTemplateDrawerOrganism` reads `?template=`+`?type=` back and renders read-only detail
 * for that card.
 */
const StudioTypeGalleryPage: React.FC<StudioTypeGalleryPageProps> = ({ builder }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const logAnalyticsEvent = useAnalytics();
  const apiClients = useApiClientsOrNull();
  const [searchParams, setSearchParams] = useSearchParams();

  const [statusFilter, setStatusFilter] = useState<StudioGalleryStatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // "New arc" (EAINT-951) — the Arc builder's only creation entry point. Immediately POSTs an
  // unpublished draft (per `createArcTemplate.ts`'s own contract) and opens straight into the
  // canvas editor; there is no separate "blank draft, name later" step like the context gallery's
  // menu, because an arc template's `systemName` (derived from the typed name) has to exist
  // before the editor route can address it.
  const [newArcOpen, setNewArcOpen] = useState(false);
  const [newArcName, setNewArcName] = useState('');
  // The archetype is required on every arc template write, and a stage-bearing archetype is
  // refused without a stage — so the dialog asks for both before the first POST, instead of
  // creating a blank row the server would reject.
  const [newArcArchetype, setNewArcArchetype] = useState<ArcArchetype | null>(null);
  const [newArcStarter, setNewArcStarter] = useState<string | null>(null);
  const [newArcCreating, setNewArcCreating] = useState(false);
  const [newArcError, setNewArcError] = useState<string | null>(null);

  const handleOpenNewArc = useCallback(() => setNewArcOpen(true), []);
  const handleCloseNewArc = useCallback(() => {
    setNewArcOpen(false);
    setNewArcName('');
    setNewArcArchetype(null);
    setNewArcStarter(null);
    setNewArcError(null);
  }, []);
  const newArcNeedsStarter = newArcArchetype !== null && archetypeRules[newArcArchetype].stagesRequired;
  const newArcReady = newArcName.trim().length > 0 && newArcArchetype !== null && (!newArcNeedsStarter || newArcStarter !== null);
  const handleSelectNewArcArchetype = useCallback((value: string) => {
    const archetype = value as ArcArchetype;
    setNewArcArchetype(archetype);
    if (!archetypeRules[archetype].stagesRequired) setNewArcStarter(null);
  }, []);
  const handleCreateArc = useCallback(async () => {
    const name = newArcName.trim();
    const archetype = newArcArchetype;
    if (!apiClients?.schemaClient || !name || !archetype) return;
    const starterId = archetypeRules[archetype].stagesRequired ? newArcStarter : null;
    if (archetypeRules[archetype].stagesRequired && !starterId) return;
    setNewArcCreating(true);
    setNewArcError(null);
    try {
      const created = await dispatch(
        createArcTemplateThunk({
          apiClient: apiClients.schemaClient,
          name,
          systemName: slugifyAspectName(name),
          definition: starterId
            ? modelToDefinition(applyStarter(definitionToModel({ name, archetype, stages: [], keyDates: {} }), starterId))
            : ({ name, archetype, stages: [], keyDates: {} } satisfies ArcTemplateDefinition),
        })
      ).unwrap();
      navigate(`/studio/arc/${encodeURIComponent(created.system_name)}/${created.version}`);
    } catch (err: unknown) {
      // The raw message is developer-facing English (axios/network text) — it is logged,
      // never rendered. The user sees the localized failure string: a 409 (the name's slug is
      // already an arc in this organization) names the collision and suggests a rename; any
      // other failure is the generic one.
      console.error('[StudioTypeGalleryPage] Failed to create arc template', err);
      const conflict = (err as { reason?: string } | null)?.reason === 'conflict';
      setNewArcError(
        conflict
          ? t('studio.gallery.newArc.errorConflict', { name, suggestion: `${name} 2` })
          : t('studio.gallery.newArc.error')
      );
    } finally {
      setNewArcCreating(false);
    }
  }, [apiClients, newArcName, newArcArchetype, newArcStarter, dispatch, navigate, t]);

  const types = useMemo(() => [...(builder.templateTypes ?? [])], [builder.templateTypes]);
  const { templates, degraded, loading, error } = useStudioTemplates(apiClients, { types });

  // Full per-card channel lists (not just counts) — the gallery's cards only need the count,
  // but the drawer's distribution section needs the channels themselves (names + links), so
  // this is the single source both derive from rather than fetching twice.
  const [distributionResults, setDistributionResults] = useState<Record<
    string,
    ChannelItemLookupEntry[]
  > | null>(null);
  const [distributionDegraded, setDistributionDegraded] = useState(false);

  useEffect(() => {
    setDistributionResults(null);
    setDistributionDegraded(false);
    if (!apiClients?.schemaClient || templates.length === 0) return;

    let cancelled = false;
    // `POST /channels/items/lookup-batch` caps a request at BATCH_LOOKUP_CHUNK items, so chunk
    // rather than truncate: a card missing from the map is indistinguishable from a card whose
    // lookup returned zero channels, and the latter renders a "Private" chip. Truncating would
    // therefore fabricate "Private" for every card past the cap (spec SC6 forbids exactly that).
    const chunks: { itemType: ChannelItemType; itemSystemName: string }[][] = [];
    for (let i = 0; i < templates.length; i += BATCH_LOOKUP_CHUNK) {
      chunks.push(
        templates.slice(i, i + BATCH_LOOKUP_CHUNK).map((card) => ({
          itemType: card.type as ChannelItemType,
          itemSystemName: card.systemName,
        })),
      );
    }

    Promise.all(
      chunks.map((chunk) => lookupChannelItemsBatch(apiClients.schemaClient, chunk)),
    )
      .then((pages) => {
        if (cancelled) return;
        const next: Record<string, ChannelItemLookupEntry[]> = {};
        pages.forEach((results) => {
          results.forEach((row) => {
            next[`${row.itemType}::${row.itemSystemName}`] = row.channels;
          });
        });
        setDistributionResults(next);
      })
      .catch(() => {
        if (cancelled) return;
        setDistributionResults(null);
        setDistributionDegraded(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClients?.schemaClient, templates]);

  const distribution = useMemo(() => {
    if (!distributionResults) return null;
    const counts: Record<string, number> = {};
    Object.entries(distributionResults).forEach(([key, channels]) => {
      counts[key] = channels.length;
    });
    return counts;
  }, [distributionResults]);

  const selectedSystemName = searchParams.get('template');
  const selectedType = searchParams.get('type');
  // `system_name` is unique per type table, not across them — the Approach builder's gallery
  // carries both `approach` and `schema`, so a systemName alone can match two cards and open
  // the wrong body. `type` narrows it; a link without one (single-type gallery, or a link
  // predating this param) still resolves by systemName.
  const selectedCard = useMemo(
    () =>
      templates.find(
        (card) =>
          card.systemName === selectedSystemName && (!selectedType || card.type === selectedType),
      ) ?? null,
    [templates, selectedSystemName, selectedType],
  );
  const selectedChannels = useMemo(() => {
    if (!selectedCard) return null;
    if (!distributionResults) return null;
    return distributionResults[distributionKey(selectedCard)] ?? [];
  }, [selectedCard, distributionResults]);

  const handleCloseDrawer = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('template');
      next.delete('type');
      return next;
    });
  };

  const viewedRef = useRef(false);
  useEffect(() => {
    if (loading || viewedRef.current) return;
    viewedRef.current = true;
    logAnalyticsEvent('studio_gallery_viewed', undefined, {
      builderId: builder.id,
      cardCount: templates.length,
      degraded,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const sections: StudioGallerySection[] = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return types.map((type) => ({
      key: type,
      labelKey: SECTION_LABEL_KEYS[type] ?? `studio.gallery.section.${type}`,
      cards: templates.filter((card) => card.type === type && matchesSearch(card, term)),
    }));
  }, [types, templates, searchTerm]);

  const handleOpen = (card: StudioTemplateCard) => {
    // Sphere types (EAINT-892) have a real read-only editor — navigate there instead of
    // opening the drawer, which has no schema body (schema cards never set ?template=).
    if (card.type === 'schema') {
      // `state.from`, not a query param — `SphereTypeEditorPage`'s back button reads this to
      // return to the gallery it was opened from.
      navigate(`/studio/types/${encodeURIComponent(card.templateId)}`, {
        state: { from: `/studio/${builder.segment}` },
      });
      return;
    }

    // Arc templates (EAINT-951) with a known version have a real editor route; a card whose
    // lifecycle is unresolved (`version: null`) falls through to the drawer below, same as
    // `studioRouteForTemplate` itself does for that case.
    if (card.type === 'arc_template' && card.version != null) {
      const route = studioRouteForTemplate({
        type: card.type,
        templateId: card.templateId,
        systemName: card.systemName,
        version: card.version,
      });
      if (route) {
        navigate(route, { state: { from: `/studio/${builder.segment}` } });
        return;
      }
    }

    logAnalyticsEvent('studio_template_drawer_opened', undefined, {
      type: card.type,
      status: card.status,
      listed: (distribution?.[distributionKey(card)] ?? 0) > 0,
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('template', card.systemName);
      next.set('type', card.type);
      return next;
    });
  };

  return (
    <div className="studio-type-gallery-page" data-testid={`studio-gallery-${builder.id}`}>
      <header className="studio-type-gallery-page__header">
        <i className={`${builder.icon} studio-type-gallery-page__icon`} aria-hidden="true" />
        <div className="studio-type-gallery-page__heading">
          <div className="studio-type-gallery-page__title-row">
            <h1 className="studio-type-gallery-page__title">{t(builder.labelKey)}</h1>
            <TagAtom label={t('studio.gallery.browseChip')} tone="info" size="small" />
          </div>
          <p className="studio-type-gallery-page__description">{t(builder.descriptionKey)}</p>
        </div>
        {builder.id === 'arc' && (
          <ButtonAtom
            variant="primary"
            size="small"
            label={t('studio.gallery.newArc.button')}
            onClick={handleOpenNewArc}
            data-testid="studio-gallery-new-arc-button"
          />
        )}
      </header>

      {builder.id === 'arc' && (
        <DialogWrapper
          header={t('studio.gallery.newArc.dialogTitle')}
          visible={newArcOpen}
          onHide={handleCloseNewArc}
        >
          <div className="studio-type-gallery-page__new-arc">
            <label className="studio-type-gallery-page__new-arc-field">
              {t('studio.gallery.newArc.nameLabel')}
              <InputAtom
                value={newArcName}
                onChange={(event) => setNewArcName(event.target.value)}
                placeholder={t('studio.gallery.newArc.namePlaceholder')}
                data-testid="studio-gallery-new-arc-name"
              />
            </label>
            <div className="studio-type-gallery-page__new-arc-field">
              <span>{t('studio.gallery.newArc.archetypeLabel')}</span>
              <ChoiceCardGroupAtom
                options={ARC_ARCHETYPES.map((archetype) => ({
                  value: archetype,
                  label: t(`studio.arcEditor.archetypes.${archetype}.label`),
                  note: t(`studio.arcEditor.archetypes.${archetype}.note`),
                }))}
                value={newArcArchetype}
                onChange={handleSelectNewArcArchetype}
                ariaLabel={t('studio.gallery.newArc.archetypeAriaLabel')}
                data-testid="studio-gallery-new-arc-archetype"
              />
            </div>
            {newArcNeedsStarter && newArcArchetype && (
              <div className="studio-type-gallery-page__new-arc-field">
                <span>{t('studio.gallery.newArc.starterLabel')}</span>
                <ChoiceCardGroupAtom
                  layout="inline"
                  options={ARC_CANVAS_STARTERS.map((starter) => ({
                    value: starter.id,
                    label: t(`studio.arcEditor.canvas.newArc.starters.${starter.id}`),
                    note: t('studio.arcEditor.canvas.newArc.starterNoteFinal', { count: starter.stageNames.length }),
                  }))}
                  value={newArcStarter}
                  onChange={setNewArcStarter}
                  ariaLabel={t('studio.gallery.newArc.starterAriaLabel')}
                  data-testid="studio-gallery-new-arc-starter"
                />
                <p className="studio-type-gallery-page__new-arc-hint">
                  {t('studio.gallery.newArc.starterHint', {
                    archetype: t(`studio.arcEditor.archetypes.${newArcArchetype}.label`),
                  })}
                </p>
              </div>
            )}
            {newArcError && (
              <MessageAtom
                severity="error"
                text={newArcError}
                data-testid="studio-gallery-new-arc-error"
              />
            )}
            <div className="studio-type-gallery-page__new-arc-actions">
              <ButtonAtom
                variant="secondary"
                size="small"
                label={t('studio.gallery.newArc.cancel')}
                onClick={handleCloseNewArc}
                data-testid="studio-gallery-new-arc-cancel"
              />
              <ButtonAtom
                variant="primary"
                size="small"
                label={newArcCreating ? t('studio.gallery.newArc.creating') : t('studio.gallery.newArc.create')}
                disabled={!newArcReady || newArcCreating}
                onClick={handleCreateArc}
                data-testid="studio-gallery-new-arc-create"
              />
            </div>
          </div>
        </DialogWrapper>
      )}

      <StudioTemplateGalleryOrganism
        sections={sections}
        loading={loading}
        errorMessage={error || degraded ? t('studio.gallery.loadFailed') : undefined}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        showStatusFilter={types.some((type) => STATUS_REPORTING_TEMPLATE_TYPES.includes(type))}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onOpen={handleOpen}
        distribution={distribution}
        distributionDegraded={distributionDegraded}
        emptyTitle={t('studio.gallery.empty.title')}
        emptyDescription={t('studio.gallery.empty.description', { builder: t(builder.labelKey) })}
      />

      <StudioTemplateDrawerOrganism
        visible={!!selectedSystemName}
        onHide={handleCloseDrawer}
        card={selectedCard}
        cardResolutionLoading={loading}
        distributionChannels={selectedChannels}
        distributionDegraded={distributionDegraded}
      />
    </div>
  );
};

export default StudioTypeGalleryPage;
